import React, { useMemo } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

const PaymentsGrid = ({ enrollments, course }) => {

    console.log('enrollments', enrollments);
    console.log('course', course);
    
  // Calcola i mesi tra startDate e endDate del corso
  const months = useMemo(() => {
    if (!course?.startDate || !course?.endDate) return [];

    const start = dayjs(course.startDate).startOf('month');
    const end = dayjs(course.endDate).startOf('month');
    const monthsList = [];

    let current = start;
    while (current.isBefore(end) || current.isSame(end, 'month')) {
      monthsList.push({
        id: current.format('YYYY-MM'),
        month: current.month(),
        year: current.year(),
        date: current.toDate(),
        label: current.locale('it').format('MMM YYYY'),
      });
      current = current.add(1, 'month');
    }

    return monthsList;
  }, [course?.startDate, course?.endDate]);

  // Calcola lo stato del pagamento per un enrollment in un mese specifico
  const getPaymentStatusForMonth = (enrollment, monthDate) => {
    if (!enrollment.paymentStatusInfo) return null;

    const enrollmentStart = dayjs(enrollment.startDate).startOf('month');
    const month = dayjs(monthDate).startOf('month');
    const today = dayjs().startOf('month');
    const paymentInterval = enrollment.paymentInterval || 1;
    const paymentsMade = enrollment.paymentStatusInfo.paymentsMade || 0;
    const paymentsLate = enrollment.paymentStatusInfo.paymentsLate || 0;

    // Calcola tutte le date previste di pagamento
    const paymentDates = [];
    for (let i = 0; i < enrollment.totalPayments; i++) {
      const paymentDate = enrollmentStart.add(i * paymentInterval, 'month');
      paymentDates.push(paymentDate.startOf('month'));
    }

    // Trova a quale periodo di pagamento appartiene questo mese
    // Un periodo inizia alla data di pagamento e dura paymentInterval mesi
    let paymentPeriodIndex = -1;
    for (let i = 0; i < paymentDates.length; i++) {
      const periodStart = paymentDates[i];
      
      // Calcola la differenza in mesi tra il mese corrente e l'inizio del periodo
      const monthsDiff = month.diff(periodStart, 'month');
      
      // Verifica se il mese appartiene a questo periodo
      if (monthsDiff >= 0) {
        // Se il mese è entro il periodo normale (0 a paymentInterval-1)
        if (monthsDiff < paymentInterval) {
          paymentPeriodIndex = i;
          break;
        }
        // Se il mese è oltre paymentInterval, verifica se questo è l'ultimo periodo
        // e non c'è un periodo successivo che inizi prima di questo mese
        if (i === paymentDates.length - 1) {
          // Questo è l'ultimo periodo, quindi copre tutti i mesi rimanenti del corso
          paymentPeriodIndex = i;
          break;
        } else {
          // C'è un periodo successivo, verifica se il mese appartiene a quello invece
          const nextPeriodStart = paymentDates[i + 1];
          if (month.isBefore(nextPeriodStart, 'month')) {
            // Il mese è prima del prossimo periodo, quindi appartiene a questo periodo
            paymentPeriodIndex = i;
            break;
          }
        }
      }
    }

    // Se il mese non appartiene a nessun periodo di pagamento
    if (paymentPeriodIndex === -1) return null;

    // Se il periodo è stato pagato (l'indice è minore di paymentsMade)
    if (paymentPeriodIndex < paymentsMade) {
      return 'paid'; // Verde
    }

    // Se il periodo non è stato pagato, verifica se è in ritardo
    // Un periodo è in ritardo se la sua data di scadenza è passata
    const periodStart = paymentDates[paymentPeriodIndex];
    const periodDueDate = periodStart; // La data di scadenza è l'inizio del periodo
    
    // Verifica se il periodo è scaduto (la data di scadenza è passata o è il mese corrente)
    const isPeriodDue = periodDueDate.isBefore(today) || periodDueDate.isSame(today, 'month');
    
    if (isPeriodDue) {
      // Conta quanti periodi non pagati ci sono prima di questo (incluso questo)
      let unpaidBeforeThis = 0;
      for (let i = paymentsMade; i <= paymentPeriodIndex; i++) {
        const pd = paymentDates[i];
        if (pd.isBefore(today) || pd.isSame(today, 'month')) {
          unpaidBeforeThis++;
        }
      }
      
      // Se questo periodo è tra i primi paymentsLate periodi in ritardo
      if (unpaidBeforeThis <= paymentsLate) {
        return 'late'; // Rosso - tutti i mesi del periodo sono rossi se il periodo è in ritardo
      }
    }

    // Periodo futuro o non ancora scaduto
    return null;
  };

  if (!enrollments || enrollments.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant='h6' gutterBottom>
          Visuale Pagamenti
        </Typography>
        <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant='body2' color='text.secondary'>
            Nessun iscritto a questo corso
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!months || months.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant='h6' gutterBottom>
          Visuale Pagamenti
        </Typography>
        <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant='body2' color='text.secondary'>
            Nessun periodo di corso definito
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant='h6' gutterBottom>
        Visuale Pagamenti
      </Typography>
      <TableContainer
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          position: 'relative',
          '&::-webkit-scrollbar': {
            height: 12,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        }}
      >
        <Table
          size='small'
          sx={{
            minWidth: { xs: 'auto', sm: Math.max(600, months.length * 80) },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  minWidth: { xs: 80, sm: 150 },
                  maxWidth: { xs: 120, sm: 'none' },
                  width: { xs: 100, sm: 'auto' },
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 2,
                  borderRight: '2px solid',
                  borderColor: 'divider',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: 'divider',
                  },
                }}
              >
                Iscritto
              </TableCell>
              {months.map((month) => (
                <TableCell
                  key={month.id}
                  align='center'
                  sx={{
                    fontWeight: 'bold',
                    minWidth: 50,
                    padding: '8px 4px',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant='body2' sx={{ fontWeight: 'bold', textTransform: 'capitalize', fontSize: '0.75rem' }}>
                      {month.label}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {enrollments.map((enrollment) => {
              const user = enrollment.user || enrollment.userId;
              const userName = typeof user === 'object' ? `${user.name} ${user.surname}` : user;

              return (
                <TableRow key={enrollment._id} hover>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      minWidth: { xs: 80, sm: 150 },
                      maxWidth: { xs: 120, sm: 'none' },
                      width: { xs: 100, sm: 'auto' },
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 1,
                      borderRight: '2px solid',
                      borderColor: 'divider',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: 'divider',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        overflow: 'hidden',
                        fontSize: { xs: '0.8rem', sm: 'inherit' },
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        textOverflow: { xs: 'clip', sm: 'ellipsis' },
                        display: { xs: '-webkit-box', sm: 'block' },
                        WebkitLineClamp: { xs: 2, sm: 'none' },
                        WebkitBoxOrient: { xs: 'vertical', sm: 'horizontal' },
                        lineHeight: 1,
                      }}
                    >
                      {userName}
                    </Box>
                  </TableCell>
                  {months.map((month) => {
                    const paymentStatus = getPaymentStatusForMonth(enrollment, month.date);

                    return (
                      <TableCell
                        key={month.id}
                        align='center'
                        sx={{
                          backgroundColor:
                            paymentStatus === 'paid'
                              ? 'success.light'
                              : paymentStatus === 'late'
                              ? 'error.light'
                              : 'inherit',
                          color:
                            paymentStatus === 'paid' || paymentStatus === 'late'
                              ? 'primary.contrastText'
                              : 'inherit',
                          minWidth: 50,
                          padding: '8px 4px',
                        }}
                      >
                        {paymentStatus === 'paid' && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              margin: '0 auto',
                              backgroundColor: 'success.main',
                            }}
                          />
                        )}
                        {paymentStatus === 'late' && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              margin: '0 auto',
                              backgroundColor: 'error.main',
                            }}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PaymentsGrid;

